import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button, TextError, TitleH1, TitleH3 } from "components/generic";
import { IPerson, IPropsFC, RoleUser, TGender } from "interfaces/intarfaces";
import {
  ContainerGeneric,
  ContainerNotice,
  ContainerRegistration,
  ContainerValue,
  ContainerValueExtra,
  FlexBox,
} from "components/containers";
import { useInput } from "components/Input";
import { useTextArea } from "components/TextArea";
import { useRange } from "components/Range";
import { useSelect } from "components/Select";
import { useRadiobox } from "components/CheckRadiobox";
import { getLanguage } from "utils/fetchMapInfo";
import { useMultiSelect } from "components/MultiSelect";

import { useContextHospi } from "context/ContextHospi";
import { UserAPI } from "API/userApi";
import { API_USERS, HOST_PROFILEVIEW_ROUTE } from "utils/constants";
import { SocialPreferences, UnauthorizedUser, } from "pages/ProfileUser/SimpleComponents";
import { PersonalDetailsEdit } from "pages/ProfileUser/PersonalDetailsEdit";
import { Comparator } from "utils/logics";
import { AboutMeComponent } from "pages/ProfileUser/AboutMeComponent";
import { AvatarCroper } from "components/AvatarCroper";
import { uploadAvatar } from "utils/uploadAvatar";
import { LoaderCustom } from "components/Loader";
import { theme } from "styles/theme";
import { TLang } from "assets/data/isoNameLanguage";

const LeftLabel: React.FC<{ keyItem: string }> = ({ keyItem }) => {
  const { t } = useTranslation("host");

  return (
    <ContainerNotice>
      <h3>{t(`hostProfile.${keyItem}.title`)}</h3>
      <p>{t(`hostProfile.${keyItem}.comment`)}</p>
    </ContainerNotice>
  );
};

export const HostProfile: React.FC = () => {
  const { t, i18n } = useTranslation("host");
  const navigate = useNavigate();
  const [responseError, setResponseError] = useState("");
  const [response401, setResponse401] = useState(false);

  const { userProfile, updateProfile, getCurrentUserID } =
    useContextHospi();
  const [currentUserID, setCurrentUserID] = useState(
    userProfile?.user_id || ""
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getCurrentUserID && setCurrentUserID(getCurrentUserID);
  }, [getCurrentUserID]);

  const titlePholder = (part: string, variable: string): IPropsFC => {
    return {
      title: t(`hostProfile.${part}.form.${variable}.title`),
      placeholder: t(`hostProfile.${part}.form.${variable}.placeholder`),
      w: "initial",
      grow: "1",
    };
  };

  const firstName = {
    ...useInput(userProfile?.first_name || ""),
    name: "first-name",
  };
  const lastName = {
    ...useInput(userProfile?.last_name || ""),
    name: "last-name",
  };
  const phoneCode = {
    ...useSelect(userProfile?.phone_code || undefined),
  };
  const phone = {
    ...useInput(userProfile?.phone_number || ""),
    name: "tel-local",
  };

  const gender = {
    ...useRadiobox("gender", userProfile?.gender || ""),
    name: "gender",
  };
  const spokenLanguage = {
    ...useMultiSelect(getLanguage(i18n.language.toUpperCase() as TLang), userProfile?.spoken_languages || [], [
      "gb",
      "nl",
      "de",
    ]),
    ...titlePholder("aboutMeInfo", "spokenLanguage"),
  };
  const shortDescription = {
    ...useTextArea(userProfile?.description || ""),
    ...titlePholder("aboutMeInfo", "aboutYourself"),
  };
  const privacy = useRange(userProfile?.privacy || 50);
  const newInTown = useRange(userProfile?.requested_autonomy || 50);

  const [photo, setPhoto] = useState<File | undefined>(undefined);

  const sendFromData = async () => {
    setLoading(true);

    const userFormData: IPerson = {
      user_id: currentUserID,
      first_name: firstName.value as string,
      last_name: lastName.value as string,
      phone_code: phoneCode.value as string,
      phone_number: phone.value as string,
      gender: gender.selected as TGender,
      spoken_languages: spokenLanguage.values,
      default_language: i18n.language,
      description: shortDescription.value as string,
      privacy: privacy.value,
      user_type: "host" as RoleUser,
      requested_autonomy: newInTown.value,
    };

    const data = {
      ...Comparator(userFormData, userProfile ?? {}, "host"),
      ...{
        user_id: currentUserID,
        privacy: privacy.value,
        user_type: "host" as RoleUser,
        requested_autonomy: newInTown.value,
      },
    };

    const response = userProfile
      ? await UserAPI.update(API_USERS + `host/${currentUserID}`, data)
      : await UserAPI.create(API_USERS + "host/", data);

    if (response.hasOwnProperty("status")) {
      if (response.status === 401) setResponse401(true);
      else
        try {
          setResponseError(
            "Something went wrong.\n" +
              (response.status > 399 && response.status < 500)
              ? response.errorMessages
                .map((line: [string, string]) => `${line[0]} - ${line[1]}`)
                .join("\n")
              : "105"
          );
        } catch (error) {
          setResponseError("Something went wrong, 104");
        }
      setLoading(false);
      return;
    }

    const [thumbnail_url, is_profile_complete] = await uploadAvatar(
      photo,
      getCurrentUserID
    );

    updateProfile({
      ...(response as IPerson),
      ...(photo
        ? {
          thumbnail_url: !!thumbnail_url.length ? thumbnail_url : undefined,
          is_profile_complete: is_profile_complete ?? false,
        }
        : {}),
    });
    setLoading(false);
    navigate(`/${i18n.language}${HOST_PROFILEVIEW_ROUTE.slice(5)}`);
  };

  const skipProfile = async () => {
    const userFormData: Partial<IPerson> = {
      user_id: currentUserID,
      privacy: privacy.value,
      user_type: "host" as RoleUser,
      requested_autonomy: newInTown.value,
    };

    if (!userProfile) {
      const response = await UserAPI.create(API_USERS + "host/", userFormData);

      if (response.hasOwnProperty("status")) return;
      updateProfile(response);
    }

    navigate(`/${i18n.language}${HOST_PROFILEVIEW_ROUTE.slice(5)}`);
  };

  const textLoader = t("textLoading.1");

  // When user Unauthorized
  if (response401) return <UnauthorizedUser />;

  return (
    <ContainerRegistration>
      <ContainerGeneric>
        <span />
        <TitleH1>{t("hostProfile.title")}</TitleH1>
        {/* <LeftLabel keyItem="registerAs" /> */}
        {loading && (
          <LoaderCustom
            size={5}
            color={theme.colors.bgBody}
            text={textLoader}
          />
        )}
        {/* <ContainerValue> */}
        {/* <FlexBox justify="space-between" gap="24px"> */}
        {/* <BorderBox active={true}>
              <Radiobox
                p="1.5rem"
                checked={true}
                selected="host"
                title={t("hostProfile.registerAs.form.host")}
                value="host"
                h="64px"
              />
            </BorderBox>
            <BorderBox active={false} onClick={() => changeUser("student")}>
              <Radiobox
                p="1.5rem"
                // checked={false}
                selected="student"
                title={t("hostProfile.registerAs.form.student")}
                value="host"
                h="64px"
              />
            </BorderBox>

            <Label>
              {t("hostProfile.introtext.intro")}
            </Label> */}
        {/* </FlexBox> */}
        {/* </ContainerValue> */}
        <LeftLabel keyItem="personalDetails" />
        <ContainerValue>
          <PersonalDetailsEdit
            firstName={firstName}
            lastName={lastName}
            phoneCode={phoneCode}
            phone={phone}
          />
        </ContainerValue>
        <LeftLabel keyItem="aboutMeInfo" />
        <ContainerValue>
          <AboutMeComponent
            gender={gender}
            spokenLanguage={spokenLanguage}
            shortDescription={shortDescription}
          />
        </ContainerValue>
        <LeftLabel keyItem="socialPreferences" />
        <ContainerValue>
          <SocialPreferences
            roleUser="host"
            privacy={privacy}
            newInTown={newInTown}
            muted={false}
          />
        </ContainerValue>
        <LeftLabel keyItem="profilePicture" />
        <ContainerValue>
          <TitleH3>{t(`hostProfile.profilePicture.form.photo.title`)}</TitleH3>
          <AvatarCroper
            getAvatar={setPhoto}
            initial={
              userProfile?.thumbnail_url
                ? [userProfile.thumbnail_url]
                : undefined
            }
          />
        </ContainerValue>
        <span />
        <ContainerValueExtra>
          <TextError>{responseError}</TextError>
          <FlexBox justify="flex-end" margin="0 0 24px 0">
            <Button normal onClick={() => skipProfile()} m="0 24px 0 0">
              {t(`hostProfile.buttonSkip.title`)}
            </Button>
            <Button
              main
              onClick={() => {
                sendFromData();
              }}
            >
              {t(`hostProfile.buttonSave.title`)}
            </Button>
          </FlexBox>
        </ContainerValueExtra>
      </ContainerGeneric>
    </ContainerRegistration>
  );
};
